import { useState, useContext, useEffect } from "react";
import {
  Grid,
  Box,
  TextField,
  Typography,
  FormControlLabel,
  Checkbox,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useNavigate } from "react-router-dom";
import { LoadingButton } from "@mui/lab";
import { AuthContext } from "../../../context/AuthContext";

const CreateCustomerDialog = ({
  open,
  defaultCompanyName = "",
  handleClose,
  onSuccess,
}) => {
  const navigate = useNavigate();
  const { authToken, ezorder } = useContext(AuthContext);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const formValidationSchema = Yup.object({
    companyName: Yup.string().required("Company Name is required"),
    ownerFullName: Yup.string().required("Name is required"),
    ownerEmail: Yup.string().email().required("Email is required"),
    creditType: Yup.string().required("Credit Type Required"),
    creditTerms: Yup.string().when("creditType", {
      is: (val) => val == "Credit Terms",
      then: () => Yup.string().required("Please select a credit term"),
      otherwise: () => Yup.string().nullable(),
    }),
  });

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      companyName: defaultCompanyName,
      // companyContactName: "", // TODO: copy owner
      // companyContactEmail: "", // TODO: copy owner
      // companyContactPhone: "", // TODO: copy owner
      isVendorOrSupplier: false,

      ownerFullName: "",
      ownerEmail: "",
      ownerPhone: "",
      ownerPassword: "",
      copyOwnerToAllNotifications: true,
      creditType: "Pay in Advance",
      creditTerms: null,
    },
    validationSchema: formValidationSchema,
    validateOnBlur: false,
    validateOnChange: true,
    onSubmit: (values) => {
      console.log("SUBMITTED", values);
      createCustomer(values);
    },
  });

  const createCustomer = async (requestBody) => {
    try {
      setLoading(true);
      setErrorMessage("");
      const response = await ezorder.post(`/admin/auth/signup`, requestBody, {
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
      });

      if (response.status == 200 || response.status == 201) {
        onSuccess(response.data.user.company);
      } else {
        setErrorMessage(response.response.data.error);
      }
    } catch (error) {
      console.log(error);
      if (error.response) {
        // The request was made and the server responded with a status code
        // that falls out of the range of 2xx
        console.log(error.response.data);
        console.log(error.response.status);
        console.log(error.response.headers);
        setErrorMessage("error", error.response.data.error);
      } else {
        // Something went really bad
        setErrorMessage("error", error);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      fullWidth
      open={open}
      onClose={() => {
        handleClose();
      }}
    >
      <DialogTitle>Add New Customer</DialogTitle>
      <DialogContent>
        <Box sx={{ width: "100%" }}>
          <Grid container spacing={2} sx={{ paddingTop: "8px" }}>
            <Grid item xs={12} sm={6}>
              <TextField
                size="small"
                required
                fullWidth
                id="companyName"
                label="Company"
                name="companyName"
                value={formik.values.companyName}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={
                  formik.touched.companyName &&
                  formik.errors.companyName &&
                  Boolean(formik.errors.companyName && true)
                }
                helperText={
                  formik.touched.companyName &&
                  formik.errors.companyName &&
                  `${formik.errors.companyName}`
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                size="small"
                required
                fullWidth
                id="ownerFullName"
                label="Full Name"
                name="ownerFullName"
                value={formik.values.ownerFullName}
                onChange={formik.handleChange}
                autoComplete="name"
                onBlur={formik.handleBlur}
                error={
                  formik.touched.ownerFullName &&
                  formik.errors.ownerFullName &&
                  Boolean(formik.errors.ownerFullName && true)
                }
                helperText={
                  formik.touched.ownerFullName &&
                  formik.errors.ownerFullName &&
                  `${formik.errors.ownerFullName}`
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                size="small"
                required
                fullWidth
                id="ownerEmail"
                label="Email Address"
                name="ownerEmail"
                value={formik.values.ownerEmail}
                onChange={formik.handleChange}
                autoComplete="email"
                onBlur={formik.handleBlur}
                error={
                  formik.touched.ownerEmail &&
                  formik.errors.ownerEmail &&
                  Boolean(formik.errors.ownerEmail && true)
                }
                helperText={
                  formik.touched.ownerEmail &&
                  formik.errors.ownerEmail &&
                  `${formik.errors.ownerEmail}`
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                size="small"
                fullWidth
                id="ownerPhone"
                label="Phone Number"
                name="ownerPhone"
                autoComplete="phone"
                value={formik.values.ownerPhone}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={
                  formik.touched.ownerPhone &&
                  formik.errors.ownerPhone &&
                  Boolean(formik.errors.ownerPhone && true)
                }
                helperText={
                  formik.touched.ownerPhone &&
                  formik.errors.ownerPhone &&
                  `${formik.errors.ownerPhone}`
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                size="small"
                select
                variant="outlined"
                label="Credit Type"
                id="creditType"
                name="creditType"
                fullWidth
                value={formik.values.creditType}
                onChange={(e) => {
                  formik.setValues({
                    ...formik.values,
                    creditType: e.target.value,
                    creditTerms:
                      e.target.value == "Credit Terms" ? "Net 30" : null,
                  });
                }}
                onBlur={formik.handleBlur}
                error={
                  formik.touched.creditType &&
                  formik.errors.creditType &&
                  Boolean(formik.errors.creditType && true)
                }
                helperText={
                  formik.touched.creditType &&
                  formik.errors.creditType &&
                  `${formik.errors.creditType}`
                }
              >
                <MenuItem value={"Pay in Advance"}>Pay in Advance</MenuItem>
                <MenuItem value={"Cash On Delivery"}>Cash On Delivery</MenuItem>
                <MenuItem value={"Credit Terms"}>Credit Terms</MenuItem>
              </TextField>
            </Grid>
            {formik.values.creditType == "Credit Terms" && (
              <Grid item xs={12} sm={6}>
                <TextField
                  size="small"
                  select
                  variant="outlined"
                  label="Credit Terms"
                  id="creditTerms"
                  name="creditTerms"
                  fullWidth
                  value={formik.values.creditTerms}
                  onChange={(e) => {
                    formik.setValues({
                      ...formik.values,
                      creditTerms: e.target.value,
                    });
                  }}
                  onBlur={formik.handleBlur}
                  error={
                    formik.touched.creditTerms &&
                    formik.errors.creditTerms &&
                    Boolean(formik.errors.creditTerms && true)
                  }
                  helperText={
                    formik.touched.creditTerms &&
                    formik.errors.creditTerms &&
                    `${formik.errors.creditTerms}`
                  }
                >
                  <MenuItem value={"Net 30"}>Net 30</MenuItem>
                  <MenuItem value={"Net 45"}>Net 45</MenuItem>
                  <MenuItem value={"Net 60"}>Net 60</MenuItem>
                </TextField>
              </Grid>
            )}
            <Grid item xs={12} sm={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formik.values.isVendorOrSupplier}
                    onChange={(e) => {
                      formik.setValues({
                        ...formik.values,
                        isVendorOrSupplier: e.target.checked,
                      });
                    }}
                  />
                }
                label="Vendor / Supplier Ordering on Behalf of Others"
              />
            </Grid>
          </Grid>

          <Box>
            {errorMessage && (
              <Box>
                <Typography color={"red"}>{errorMessage}</Typography>
              </Box>
            )}
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <LoadingButton
          loading={loading}
          onClick={() => {
            handleClose();
          }}
        >
          Cancel
        </LoadingButton>
        <LoadingButton
          loading={loading}
          variant="contained"
          color="primary"
          onClick={() => {
            formik.handleSubmit();
          }}
        >
          Submit
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

export default CreateCustomerDialog;
